<template>
  <b-card
    v-if="!!option"
    id="page-company-form"
    data-tour="employee-form-page"
    no-body
    class="mb-0"
  >
    <b-card-header>
      <b-card-title
        ><h2>
          {{ $t(getTitle()) }} {{ $t("pages.employees.title").toLowerCase() }}
        </h2></b-card-title
      >
      <div class="d-flex align-items-center flex-wrap justify-content-end">
        <tutorial-button
          data-tour="employee-form-help"
          class="mr-1 mb-50"
          :label="$t('tutorials.common.viewTutorial')"
          :disabled="!tutorialReady || loading"
          @click="startTutorial"
        />
        <b-button
          data-tour="employee-form-back"
          variant="primary"
          class="mb-50"
          @click="goBack"
        >
          <feather-icon icon="ArrowLeftIcon" />
          {{ $t("back") }}
        </b-button>
      </div>
    </b-card-header>

    <b-card-body>
      <form-wizard
        ref="form"
        data-tour="employee-form-wizard"
        color="#44b8ff"
        :title="null"
        :subtitle="null"
        shape="square"
        class="mb-3 shadow-none"
        @on-change="onWizardTabChange"
      >
        <tab-content
          :title="$t('pages.employees.single')"
          :before-change="validationEmployeeTab"
        >
          <validation-observer
            ref="employeeRules"
            data-tour="employee-form-details"
            tag="form"
          >
            <b-form>
              <b-row>
                <b-col
                  data-tour="employee-form-photo"
                  class="p-0"
                  cols="12"
                  md="6"
                >
                  <b-form-group
                    :label="$t(label('urlPhoto'))"
                    label-for="urlPhoto"
                  >
                    <b-media class="body-style">
                      <div v-if="!employee.urlPhoto">
                        <input
                          id="inputFilePhoto"
                          ref="fileInputPhoto"
                          name="inputFilePhoto"
                          type="file"
                          class="hidden"
                          accept=".png,.jpeg,.jpg"
                          @change="onFileChange($event, 'urlPhoto')"
                          :disabled="!isSaveMode"
                        />
                        <div
                          class="cursor-pointer m-auto cuve text-center border-2 d-flex align-items-center justify-content-center"
                          accept=".png,.jpeg,.jpg"
                          @drop="handleDrop($event, 'urlPhoto')"
                          @dragover="handleDragover"
                          @dragenter="handleDragover"
                          @click="$refs.fileInputPhoto.click()"
                        >
                          <feather-icon
                            icon="UploadCloudIcon"
                            size="20"
                            svg-classes="h-16 w-16 stroke-current text-grey"
                          />
                          <span
                            style="margin-left: 5px"
                            @click.stop="$refs.fileInputPhoto.click()"
                            >{{ $t(label("urlPhoto")) }}
                          </span>
                        </div>
                      </div>

                      <template v-if="employee.urlPhoto" #aside>
                        <div class="image-full-screen">
                          <img
                            id="image"
                            ref="image"
                            :src="employee.urlPhoto"
                            class="img-avatar border-dashed"
                          />
                        </div>
                        <div
                          v-if="employee.urlPhoto && option != 2"
                          class="d-flex flex-wrap ml-1"
                          style="width: 10px"
                        >
                          <b-button
                            variant="danger"
                            style="height: 30px; width: 30px; padding: 4px"
                            @click="deleteFile('urlPhoto')"
                            :disabled="!isSaveMode"
                          >
                            <feather-icon icon="XIcon" size="21" />
                          </b-button>
                        </div>
                      </template>
                    </b-media>
                  </b-form-group>
                </b-col>

                <b-col
                  data-tour="employee-form-contact"
                  class="p-0"
                  cols="12"
                  md="6"
                >
                  <b-col cols="12">
                    <b-form-group :label="$t(label('name'))" label-for="name">
                      <validation-provider
                        #default="{ errors }"
                        :name="$t(label('name'))"
                        rules="required"
                      >
                        <b-form-input
                          id="name"
                          v-model="employee.name"
                          :state="errors.length > 0 ? false : null"
                          :placeholder="$t(label('name'))"
                          :disabled="!isSaveMode"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12">
                    <b-form-group
                      :label="$t(label('email'))"
                      label-for="email"
                      rules="required|email"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t(label('email'))"
                        rules="required|email"
                      >
                        <b-form-input
                          id="email"
                          v-model="employee.email"
                          type="email"
                          :state="errors.length > 0 ? false : null"
                          :placeholder="$t(label('email'))"
                          :disabled="!isSaveMode"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-col>
                <b-col data-tour="employee-form-phone" cols="12" md="6">
                  <b-form-group :label="$t(label('phone'))" label-for="phone">
                    <vue-tel-input
                      inputId="phone"
                      name="phone"
                      v-model="employee.phone"
                      :disabled="!isSaveMode"
                      :mode="'international'"
                      defaultCountry="ve"
                      :disabledFetchingCountry="true"
                      :inputOptions="inputTelOption"
                      @open="onDropdownOpen(true)"
                      @close="onDropdownOpen(false)"
                    >
                      <template v-slot:arrow-icon>
                        <span>{{ openTelInput ? "▲" : "▼" }}</span>
                      </template>
                    </vue-tel-input>
                  </b-form-group>
                </b-col>
                <b-col data-tour="employee-form-role" cols="12" md="6">
                  <validation-provider
                    #default="{ errors }"
                    :name="$t(label('role'))"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t(label('role'))"
                      label-for="role"
                      :state="errors.length > 0 ? false : null"
                    >
                      <v-select
                        input-id="role"
                        ref="role"
                        v-model="employee.roles"
                        :options="allCharges"
                        label="name"
                        multiple
                        :placeholder="$t(label('role'))"
                        :disabled="!isSaveMode"
                        :reduce="(option) => option.id"
                      >
                        <span slot="no-options">{{ $t("sorryOption") }}</span>
                      </v-select>
                      <b-form-invalid-feedback
                        :state="errors.length > 0 ? false : null"
                      >
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col
                  data-tour="employee-form-schedule-type"
                  cols="12"
                  md="6"
                >
                  <validation-provider
                    #default="{ errors }"
                    :name="$t(label('scheduleType'))"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t(label('scheduleType'))"
                      label-for="scheduleType"
                      :state="errors.length > 0 ? false : null"
                    >
                      <v-select
                        input-id="scheduleType"
                        v-model="employee.idCatScheduleType"
                        :options="scheduleTypes"
                        label="name"
                        :reduce="(scheduleType) => scheduleType.id"
                        :placeholder="$t(label('scheduleType'))"
                        :disabled="!isSaveMode"
                      >
                        <span slot="no-options">{{ $t("sorryOption") }}</span>
                      </v-select>
                      <b-form-invalid-feedback
                        :state="errors.length > 0 ? false : null"
                      >
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                      <small v-if="scheduleTypeDescription" class="text-muted">
                        {{ scheduleTypeDescription }}
                      </small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col v-if="option != 1" cols="12" md="6">
                  <!-- <validation-provider
                    #default="{ errors }"
                    :name="$t(label('services'))"
                    rules="required"
                  > -->
                  <b-form-group
                    :label="$t(label('services'))"
                    label-for="services"
                  >
                    <v-select
                      input-id="services"
                      ref="services"
                      v-model="employee.services"
                      :options="servicesEmployee"
                      label="name"
                      :placeholder="$t(label('services'))"
                      :disabled="true"
                      :noDrop="true"
                      multiple
                      @option:selecting="onSelecting"
                      @input="onInput"
                      @option:selected="onSelected"
                    >
                      <span slot="no-options">{{ $t("sorryOption") }}</span>
                    </v-select>
                    <!-- <b-form-invalid-feedback
                        :state="errors.length > 0 ? false : null"
                      >
                        {{ errors[0] }}
                      </b-form-invalid-feedback> -->
                  </b-form-group>
                  <!-- </validation-provider> -->
                </b-col>
                <b-col cols="12" md="2">
                  <validation-provider
                    #default="{ errors }"
                    :name="$t(label('repose'))"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t(label('repose'))"
                      label-for="repose"
                      :state="errors.length > 0 ? false : null"
                    >
                      <b-form-checkbox
                        id="repose"
                        v-model="employee.repose"
                        switch
                        inline
                        @input.native="changeSatus($event)"
                        :disabled="!isSaveMode"
                      >
                        {{ isStatus(employee.repose) }}
                      </b-form-checkbox>
                      <b-form-invalid-feedback
                        :state="errors.length > 0 ? false : null"
                      >
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col cols="12" md="4" v-if="employee.repose">
                  <b-form-group
                    :label="$t(label('endDateRepose'))"
                    label-for="startTime"
                  >
                    <validation-provider
                      #default="{ errors }"
                      :name="$t(label('endDateRepose'))"
                      :rules="{
                        isBefore: {
                          targetField: $t(label('endDateRepose')),
                          targetValue: endTime,
                        },
                      }"
                    >
                      <b-form-datepicker
                        id="endDateRepose"
                        ref="endDateRepose"
                        v-model="employee.endDateRepose"
                        :placeholder="$t(label('endDateRepose'))"
                        :state="errors.length > 0 ? false : null"
                        :disabled="!isSaveMode"
                        class="mr-2"
                        reset-button
                        dropright
                        :min="new Date()"
                      >
                      </b-form-datepicker>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="2">
                  <validation-provider
                    #default="{ errors }"
                    :name="$t(label('isSucursal'))"
                  >
                    <b-form-group
                      :label="$t(label('isSucursal'))"
                      label-for="isSucursal"
                      :state="errors.length > 0 ? false : null"
                    >
                      <b-form-checkbox
                        id="isSucursal"
                        v-model="employee.isSucursal"
                        switch
                        inline
                        @input.native="changeSatusSucursal($event)"
                        :disabled="!isSaveMode"
                      >
                        {{ isStatusSucursal(employee.isSucursal) }}
                      </b-form-checkbox>
                      <b-form-invalid-feedback
                        :state="errors.length > 0 ? false : null"
                      >
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col cols="12" md="4" v-if="employee.isSucursal">
                  <b-form-group
                    :label="$t(label('address'))"
                    label-for="startTime"
                  >
                    <validation-provider
                      #default="{ errors }"
                      :name="$t(label('address'))"
                    >
                      <b-form-textarea
                        id="sucursalAdress"
                        v-model="employee.sucursalAdress"
                        :placeholder="$t(label('address'))"
                        rows="3"
                        :disabled="!isSaveMode"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-form>
          </validation-observer>
        </tab-content>
        <tab-content
          :title="$t('pages.employees.scheduleConfiguration')"
          ref="formWorkSchedules"
        >
          <div data-tour="employee-schedule-tab">
            <b-row
              v-if="isWeeklySchedule"
              data-tour="employee-weekly-schedule"
            >
            <!-- Día de atención del employee -->
            <b-col cols="12" md="4" v-if="option != 2">
              <b-form-group :label="$t(label('day'))" label-for="day">
                <v-select
                  ref="day"
                  v-model="catDay"
                  input-id="day"
                  :options="allDays"
                  label="name"
                  :placeholder="$t(label('day'))"
                  :reduce="(option) => option"
                >
                  <span slot="no-options">{{ $t("sorryOption") }}</span>
                </v-select>
              </b-form-group>
            </b-col>

            <!-- Inicio de atención del employee -->
            <b-col cols="12" md="4" v-if="option != 2">
              <b-form-group
                :label="$t(label('startTime'))"
                label-for="startTime"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t(label('startTime'))"
                >
                  <!--                   :rules="{
                    isBefore: {
                      targetField: $t(label('endTime')),
                      targetValue: endTime,
                    },
                  }" -->
                  <b-form-timepicker
                    id="startTime"
                    ref="startTime"
                    v-model="startTime"
                    :placeholder="$t(label('startTime'))"
                    :state="errors.length > 0 ? false : null"
                    reset-button
                    :disabled="!isSaveMode"
                    dropright
                  >
                  </b-form-timepicker>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Fin de atención del employee -->
            <b-col cols="12" md="4" v-if="option != 2">
              <b-form-group :label="$t(label('endTime'))" label-for="endTime">
                <validation-provider
                  #default="{ errors }"
                  :name="$t(label('endTime'))"
                >
                  <!-- :rules="{
                    isAfter: {
                      targetField: $t(label('startTime')),
                      targetValue: startTime,
                    },
                  }" -->
                  <b-form-timepicker
                    id="endTime"
                    ref="endTime"
                    v-model="endTime"
                    :placeholder="$t(label('endTime'))"
                    :state="errors.length > 0 ? false : null"
                    reset-button
                    :disabled="!isSaveMode"
                    dropright
                  >
                  </b-form-timepicker>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col class="d-flex justify-content-end" cols="12">
              <b-button
                v-if="option != 2"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                type="button"
                @click="addSchedules"
              >
                <feather-icon icon="PlusIcon" class="mr-25" />
                <span>{{ $t("add") }}</span>
              </b-button>
            </b-col>

            <b-col
              data-tour="employee-weekly-results"
              class="mt-1"
              cols="12"
            >
              <b-table
                id="schedules-table"
                data-tour="employee-weekly-table"
                responsive
                :striped="striped"
                :bordered="bordered"
                :borderless="borderless"
                :outlined="outlined"
                :per-page="perPage"
                :busy="loading"
                :items="employee.workSchedules"
                :fields="schedulesFields"
                class="d-none d-md-table shadow-sm !rounded"
              >
                <template #head()="data">
                  <span class="text-black">{{
                    $t(labelHeader(data.label.toLowerCase()))
                  }}</span>
                </template>

                <template #cell(nameDay)="data">
                  <slot name="cell-nameDay" :data="data">
                    {{ data.item.nameDay || "N/A" }}
                  </slot>
                </template>

                <template #cell(startTime)="data">
                  <slot name="cell-startTime" :data="data">
                    {{
                      data.item.startTime != null
                        ? formattedTime(data.item.startTime)
                        : "N/A"
                    }}
                  </slot>
                </template>

                <template #cell(endTime)="data">
                  <slot name="cell-endTime" :data="data">
                    {{
                      data.item.endTime != null
                        ? formattedTime(data.item.endTime)
                        : "N/A"
                    }}
                  </slot>
                </template>

                <template #cell(actions)="data" v-if="option != 2">
                  <feather-icon
                    class="text-primary mr-25 cursor-pointer"
                    aria-label="Edit Schedules"
                    size="18"
                    icon="EditIcon"
                    @click="onEditSchedules(data.item)"
                  />

                  <feather-icon
                    class="text-danger cursor-pointer"
                    aria-label="Delete Schedules"
                    size="18"
                    icon="Trash2Icon"
                    @click="onDeleteSchedules(data.item)"
                  />
                </template>
              </b-table>

              <!-- List for mobile devices -->
              <div
                id="list-container"
                data-tour="employee-weekly-mobile-list"
                class="d-block d-md-none mt-1 list-container"
              >
                <b-list-group>
                  <b-list-group-item
                    v-for="(item, key) in employee.workSchedules"
                    :key="key"
                    class="d-flex flex-column align-items-start border border-primary rounded mb-1"
                  >
                    <div class="d-flex w-100 justify-content-between mb-1">
                      <strong> {{ $t(labelHeader("day")) }}: </strong
                      >{{ item.nameDay || "N/A" }}
                      <b-dropdown
                        variant="link"
                        no-caret
                        class="chart-dropdown"
                        toggle-class="p-0"
                        right
                      >
                        <template #button-content>
                          <feather-icon
                            icon="MoreVerticalIcon"
                            size="18"
                            class="text-body cursor-pointer"
                          />
                        </template>
                        <b-dropdown-item @click="onDeleteSchedules(item)">
                          <div
                            class="d-flex align-items-center justify-content-between"
                          >
                            <feather-icon
                              class="text-danger"
                              size="18"
                              icon="Trash2Icon"
                            />
                            {{ $t("delete") }}
                          </div>
                        </b-dropdown-item>
                      </b-dropdown>
                    </div>
                    <p class="m-0">
                      <strong>{{ $t(labelHeader("starttime")) }}: </strong>
                      {{
                        item.startTime != null
                          ? formattedTime(item.startTime)
                          : "N/A"
                      }}
                    </p>
                    <p class="m-0">
                      <strong>{{ $t(labelHeader("endtime")) }}: </strong>
                      {{
                        item.endTime != null
                          ? formattedTime(item.endTime)
                          : "N/A"
                      }}
                    </p>
                  </b-list-group-item>
                  <b-list-group-item
                    v-if="employee.workSchedules.length == 0"
                    class="text-center"
                  >
                    {{ $t("notRegister") }}
                  </b-list-group-item>
                  <b-list-group-item v-if="loading" class="text-center">
                    <b-spinner
                      class="align-middle ml-50"
                      variant="primary"
                    ></b-spinner>
                  </b-list-group-item>
                </b-list-group>
              </div>
            </b-col>
            </b-row>

            <div
              v-else-if="isCalendarSchedule"
              data-tour="employee-calendar-schedule"
            >
              <employee-work-calendar
                ref="workCalendar"
                v-model="employee.workCalendar"
                :disabled="!isSaveMode"
                :loading="workCalendarLoading"
                :scheduled-items="scheduledWorkCalendarItems"
                :remote-scheduled-items="option !== 1"
                :scheduled-items-loading="scheduledWorkCalendarLoading"
                :scheduled-items-loaded="scheduledWorkCalendarLoaded"
                :scheduled-items-page="scheduledWorkCalendarPage"
                :scheduled-items-per-page="scheduledWorkCalendarRows"
                :scheduled-items-total="scheduledWorkCalendarTotalRows"
                @range-change="loadWorkCalendarRange"
                @calendar-change="onWorkCalendarChange"
                @day-consult="onWorkCalendarDayConsult"
                @scheduled-page-change="loadScheduledWorkCalendarPage"
              />
            </div>

            <b-alert
              v-else-if="isNoSchedule"
              data-tour="employee-no-schedule"
              show
              variant="light"
              class="schedule-mode-empty text-center mb-0"
            >
              <feather-icon icon="CalendarIcon" size="38" class="text-muted" />
              <h4 class="mt-1 mb-50">
                {{ $t("pages.employees.noSchedule.title") }}
              </h4>
              <p class="text-muted mb-0">
                {{ $t("pages.employees.noSchedule.description") }}
              </p>
            </b-alert>

            <b-alert
              v-else
              data-tour="employee-select-schedule"
              show
              variant="light-primary"
              class="schedule-mode-empty text-center mb-0"
            >
              <feather-icon icon="ClockIcon" size="38" />
              <h4 class="mt-1 mb-50">
                {{ $t("pages.employees.selectScheduleType.title") }}
              </h4>
              <p class="mb-0">
                {{ $t("pages.employees.selectScheduleType.description") }}
              </p>
            </b-alert>
          </div>
        </tab-content>

        <template slot="footer" slot-scope="props">
          <div
            data-tour="employee-form-navigation"
            class="d-flex justify-content-between w-100"
          >
            <div class="wizard-footer-left">
              <wizard-button
                v-if="props.activeTabIndex > 0 && !props.isLastStep"
                @click.native="props.prevTab()"
                :style="props.fillButtonStyle"
              >
                {{ $t("back") }}
              </wizard-button>

              <wizard-button
                v-else-if="props.activeTabIndex > 0 && props.isLastStep"
                @click.native="props.prevTab()"
                :style="props.fillButtonStyle"
              >
                {{ $t("back") }}
              </wizard-button>
            </div>
            <div class="wizard-footer-right">
              <wizard-button
                v-if="!props.isLastStep"
                @click.native="props.nextTab()"
                class="wizard-footer-right"
                :style="props.fillButtonStyle"
              >
                {{ $t("following") }}
              </wizard-button>

              <wizard-button
                v-else-if="option != 2"
                @click.native="onSubmit"
                class="wizard-footer-right finish-button"
                :style="props.fillButtonStyle"
              >
                {{ props.isLastStep ? $t("save") : $t("following") }}
              </wizard-button>
            </div>
          </div>
        </template>
      </form-wizard>
    </b-card-body>

    <app-tour
      :name="tutorialName"
      :steps="tutorialSteps"
      :options="tutorialOptions"
      :callbacks="tutorialCallbacks"
      @target-not-found="onTutorialTargetNotFound"
    />
  </b-card>
</template>

<script>
import _ from "lodash";
import "vue-form-wizard/dist/vue-form-wizard.min.css";
import { FormWizard, TabContent, WizardButton } from "vue-form-wizard";
import {
  ValidationProvider,
  ValidationObserver,
  localize,
  extend,
} from "vee-validate";
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BFormGroup,
  BFormCheckbox,
  BForm,
  BFormTextarea,
  BAlert,
  BFormSelect,
  BModal,
  BToast,
  BFormRadio,
  BFormRadioGroup,
  BFormInvalidFeedback,
  BFormTimepicker,
  BListGroup,
  BListGroupItem,
  BFormDatepicker,
} from "bootstrap-vue";
import router from "@/router";
import Ripple from "vue-ripple-directive";
import { required, email } from "@validations";
import vSelect from "vue-select";
import { allCharges } from "@core/api/request/charges";
import {
  getParams,
  getParamsAll,
  showLoading,
  stopLoading,
} from "@core/utils/utils";
import { getCatalog, postFile } from "@core/api/request/catalogs";
import EmployeeWorkCalendar from "./EmployeeWorkCalendar.vue";
import AppTour from "@core/components/app-tour/AppTour.vue";
import TutorialButton from "@core/components/app-tour/TutorialButton.vue";
import { buildEmployeeFormTour } from "@/tutorials/employees";

import {
  getEmployee,
  createOrEditEmployee,
  getWorkSchedules,
  createWorkSchedules,
  editWorkSchedules,
  deleteWorkSchedules,
  getServicesByEmployee,
  getWorkCalendarByEmployeeByRange,
} from "@core/api/request/employees";

import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";
import moment from "moment";

extend("isBefore", {
  params: ["targetField", "targetValue"],
  validate(value, { targetValue }) {
    if (!targetValue) return true;
    return value < targetValue;
  },
  message:
    "{_field_} debe ser menor que {targetField} y no pueden ser iguales.",
});

extend("isAfter", {
  params: ["targetField", "targetValue"],
  validate(value, { targetValue }) {
    if (!targetValue) return true;
    return value > targetValue;
  },
  message:
    "{_field_} debe ser mayor que {targetField} y no pueden ser iguales.",
});

const employee = {
  idEmployee: null,
  guidCompany: null,
  name: "",
  phone: "",
  email: "",
  idCatRole: 0,
  roleName: null,
  workSchedules: [],
  workCalendar: [],
  idCatScheduleType: null,
  services: [],
  repose: false,
  endDateRepose: null,
  roles: [],
  isSucursal: false,
  sucursalAdress: null,
  urlPhoto: null,
};

const diasSemana = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];

export default {
  components: {
    BFormRadio,
    BFormRadioGroup,
    BAlert,
    BModal,
    ValidationProvider,
    ValidationObserver,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BForm,
    vSelect,
    BFormTextarea,
    BFormSelect,
    BToast,
    BFormInvalidFeedback,
    VueTelInput,
    FormWizard,
    TabContent,
    WizardButton,
    BFormTimepicker,
    BListGroup,
    BListGroupItem,
    BFormDatepicker,
    EmployeeWorkCalendar,
    AppTour,
    TutorialButton,
  },
  directives: {
    Ripple,
  },
  data: () => ({
    loading: false,
    isSaveMode: true,
    required,
    perPage: 50,
    headVariant: "light",
    striped: false,
    outlined: false,
    bordered: true,
    borderless: true,
    email,
    option: "",
    employee: Object.assign(employee),
    companies: [],
    allCharges: [],
    roles: [],
    roleSelect: null,
    inputTelOption: {
      placeholder: "Ingrese un número de teléfono",
    },
    openTelInput: false,
    allDays: [],
    scheduleTypes: [],
    catDay: Object.assign({}),
    idOrUuid: null,
    startTime: null,
    endTime: null,
    itemsPage: 1,
    itemsRows: 999,
    schedulesFields: [
      { label: "day", key: "nameDay", sortable: false },
      { label: "starttime", key: "startTime", sortable: false },
      { label: "endtime", key: "endTime", sortable: false },
      { label: "action", key: "actions", sortable: false },
    ],
    servicesEmployee: [],
    itemSelected: null,
    idEmployee: null,
    workCalendarLoadedRanges: [],
    workCalendarPendingRanges: [],
    workCalendarDirtyDates: [],
    workCalendarDeletedDates: [],
    workCalendarRequestGeneration: 0,
    workCalendarRangeRows: 999,
    scheduledWorkCalendarItems: [],
    scheduledWorkCalendarLoading: false,
    scheduledWorkCalendarLoaded: false,
    scheduledWorkCalendarPage: 1,
    scheduledWorkCalendarRows: 5,
    scheduledWorkCalendarTotalRows: 0,
    scheduledWorkCalendarEmployeeId: null,
    scheduledWorkCalendarDate: null,
    scheduledWorkCalendarRequestGeneration: 0,
    activeWizardTab: 0,
    isMobileViewport: false,
    tutorialName: "employeeFormTour",
    tutorialReady: false,
  }),
  computed: {
    tutorialMode() {
      return {
        1: "create",
        2: "show",
        3: "edit",
      }[this.option] || "create";
    },
    tutorialScheduleMode() {
      if (this.isWeeklySchedule) return "weekly";
      if (this.isCalendarSchedule) return "calendar";
      if (this.isNoSchedule) return "none";
      return "unselected";
    },
    tutorialSteps() {
      return buildEmployeeFormTour({
        t: this.$t.bind(this),
        mode: this.tutorialMode,
        activeTab: this.activeWizardTab,
        scheduleMode: this.tutorialScheduleMode,
        isMobile: this.isMobileViewport,
      });
    },
    tutorialOptions() {
      return {
        highlight: true,
        enableScrolling: true,
        useKeyboardNavigation: true,
        startTimeout: 150,
        stopOnTargetNotFound: true,
      };
    },
    tutorialCallbacks() {
      return {
        onFinish: this.onTutorialFinish,
      };
    },
    canNavigateWizardFreely() {
      return this.option === 2 || this.option === 3;
    },
    workCalendarLoading() {
      return this.workCalendarPendingRanges.length > 0;
    },
    selectedScheduleType() {
      return this.scheduleTypes.find(
        (scheduleType) =>
          String(scheduleType.id) === String(this.employee.idCatScheduleType),
      );
    },
    selectedScheduleTypeName() {
      return String(this.selectedScheduleType?.name || "")
        .trim()
        .toLocaleLowerCase("es");
    },
    isWeeklySchedule() {
      return this.selectedScheduleTypeName === "semanal";
    },
    isCalendarSchedule() {
      return this.selectedScheduleTypeName === "calendario";
    },
    isNoSchedule() {
      return this.selectedScheduleTypeName === "ninguno";
    },
    scheduleTypeDescription() {
      if (this.isWeeklySchedule) {
        return this.$t("pages.employees.scheduleTypes.weeklyDescription");
      }
      if (this.isCalendarSchedule) {
        return this.$t("pages.employees.scheduleTypes.calendarDescription");
      }
      if (this.isNoSchedule) {
        return this.$t("pages.employees.scheduleTypes.noneDescription");
      }
      return "";
    },
  },

  created() {
    localize("es");
    this.initialize().finally(() => {
      this.tutorialReady = true;
    });
  },
  watch: {
    "$store.state.appConfig.activeCompanyGuid"(guid) {
      this.employee.guidCompany = guid;
    },
  },

  mounted() {
    this.updateViewportMode();
    window.addEventListener("resize", this.updateViewportMode);
    this.enableWizardFreeNavigation();
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.updateViewportMode);
  },

  methods: {
    onWizardTabChange(previousIndex, nextIndex) {
      this.activeWizardTab = nextIndex;
      if (nextIndex !== 1 || !this.isCalendarSchedule) return;
      this.$nextTick(() => {
        this.$refs.workCalendar?.refreshCalendarSize();
      });
    },
    updateViewportMode() {
      this.isMobileViewport = window.innerWidth < 768;
    },
    async startTutorial() {
      if (!this.tutorialReady || this.loading) return;

      this.updateViewportMode();
      await this.$nextTick();

      const tour = this.$tours[this.tutorialName];
      if (!tour || !this.tutorialSteps.length) return;

      const allTargetsExist = this.tutorialSteps.every((step) =>
        document.querySelector(step.target),
      );

      if (!allTargetsExist) {
        this.onTutorialTargetNotFound();
        return;
      }

      try {
        await tour.start();
      } catch (error) {
        this.onTutorialTargetNotFound();
      }
    },
    onTutorialFinish() {
      const idUser =
        this.$store.state.appConfig.sessionContext?.idUser || "user";
      localStorage.setItem(
        `sofia:tutorial:${idUser}:employees:form:${this.tutorialMode}:v1`,
        "completed",
      );
    },
    onTutorialTargetNotFound() {
      this.alertToast(
        "warning",
        this.$t("tutorials.common.unavailableTitle"),
        this.$t("tutorials.common.unavailableContent"),
        "AlertTriangleIcon",
      );
    },
    calendarDate(value) {
      if (!value) return "";
      const rawDate = String(value);
      const datePart = rawDate.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
      if (datePart) return datePart;

      const date = moment(rawDate);
      return date.isValid() ? date.format("YYYY-MM-DD") : "";
    },
    createWorkCalendarUuid(workDate, index = 0) {
      const date = this.calendarDate(workDate) || "date";
      return `${date}-${Date.now()}-${index}-${Math.random()
        .toString(16)
        .slice(2)}`;
    },
    normalizeWorkCalendarItems(items) {
      if (!Array.isArray(items)) return [];
      return items.map((item, index) => {
        if (item?.idWorkCalendar || item?.uuid) return item;
        return {
          ...item,
          uuid: this.createWorkCalendarUuid(item?.workDate, index),
        };
      });
    },
    resetWorkCalendarRequestState() {
      this.workCalendarRequestGeneration += 1;
      this.scheduledWorkCalendarRequestGeneration += 1;
      this.workCalendarLoadedRanges = [];
      this.workCalendarPendingRanges = [];
      this.workCalendarDirtyDates = [];
      this.workCalendarDeletedDates = [];
      this.scheduledWorkCalendarItems = [];
      this.scheduledWorkCalendarLoading = false;
      this.scheduledWorkCalendarLoaded = false;
      this.scheduledWorkCalendarPage = 1;
      this.scheduledWorkCalendarTotalRows = 0;
      this.scheduledWorkCalendarEmployeeId = null;
      this.scheduledWorkCalendarDate = null;
    },
    onWorkCalendarChange({ type, entry }) {
      const date = this.calendarDate(entry?.workDate);
      if (!date) return;

      if (!this.workCalendarDirtyDates.includes(date)) {
        this.workCalendarDirtyDates.push(date);
      }

      if (type === "delete") {
        if (!this.workCalendarDeletedDates.includes(date)) {
          this.workCalendarDeletedDates.push(date);
        }
      } else {
        this.workCalendarDeletedDates = this.workCalendarDeletedDates.filter(
          (deletedDate) => deletedDate !== date,
        );
      }

      if (
        this.scheduledWorkCalendarLoaded &&
        this.scheduledWorkCalendarDate === date
      ) {
        const itemIndex = this.scheduledWorkCalendarItems.findIndex(
          (item) =>
            (entry.idWorkCalendar &&
              String(item.idWorkCalendar) ===
                String(entry.idWorkCalendar)) ||
            (entry.uuid && item.uuid === entry.uuid),
        );

        if (type === "delete" && itemIndex !== -1) {
          this.scheduledWorkCalendarItems.splice(itemIndex, 1);
          this.scheduledWorkCalendarTotalRows = Math.max(
            0,
            this.scheduledWorkCalendarTotalRows - 1,
          );
        } else if (type === "update" && itemIndex !== -1) {
          this.$set(this.scheduledWorkCalendarItems, itemIndex, entry);
        } else if (type === "create") {
          this.scheduledWorkCalendarItems.unshift(entry);
          this.scheduledWorkCalendarItems =
            this.scheduledWorkCalendarItems.slice(
              0,
              this.scheduledWorkCalendarRows,
            );
          this.scheduledWorkCalendarTotalRows += 1;
        }
      }
    },
    extractWorkCalendarItems(response) {
      const data = _.get(response, "data");
      if (Array.isArray(data)) return this.normalizeWorkCalendarItems(data);
      if (Array.isArray(data?.items)) {
        return this.normalizeWorkCalendarItems(data.items);
      }
      if (Array.isArray(data?.listOfItems?.items)) {
        return this.normalizeWorkCalendarItems(data.listOfItems.items);
      }
      return [];
    },
    extractWorkCalendarPage(response, requestedPage) {
      const data = _.get(response, "data") || {};
      const pageData = data.listOfItems || data;
      const items = this.extractWorkCalendarItems({ data: pageData });
      const totalRows = Number(pageData.totalRows);
      return {
        items,
        currentPage:
          Number(pageData.currentPage || pageData.itemsPage) || requestedPage,
        totalRows: Number.isFinite(totalRows) ? totalRows : items.length,
      };
    },
    mergeScheduledItemsIntoWorkCalendar(items) {
      const entriesByDate = new Map();
      const currentItems = Array.isArray(this.employee.workCalendar)
        ? this.employee.workCalendar
        : [];

      [...items, ...currentItems].forEach((item) => {
        const date = this.calendarDate(item?.workDate);
        if (date) entriesByDate.set(date, item);
      });

      this.employee.workCalendar = [...entriesByDate.values()];
    },
    onWorkCalendarDayConsult(entry) {
      if (this.option === 1 || !entry) return;
      const idEmployee = entry.idEmployee || this.employee.idEmployee;
      const workDate = this.calendarDate(entry.workDate);
      if (!idEmployee || !workDate) return;

      if (
        this.scheduledWorkCalendarLoaded &&
        String(this.scheduledWorkCalendarEmployeeId) === String(idEmployee) &&
        this.scheduledWorkCalendarDate === workDate
      ) {
        return;
      }
      this.loadScheduledWorkCalendar(workDate, idEmployee, 1);
    },
    loadScheduledWorkCalendarPage(page) {
      if (
        !this.scheduledWorkCalendarDate ||
        !this.scheduledWorkCalendarEmployeeId
      ) {
        return;
      }
      this.loadScheduledWorkCalendar(
        this.scheduledWorkCalendarDate,
        this.scheduledWorkCalendarEmployeeId,
        page,
      );
    },
    async loadScheduledWorkCalendar(workDate, idEmployee, page = 1) {
      if (this.option === 1 || !idEmployee || !workDate) {
        return;
      }

      const selectedDate = moment(workDate, "YYYY-MM-DD", true);
      if (!selectedDate.isValid()) return;

      const requestGeneration =
        this.scheduledWorkCalendarRequestGeneration + 1;
      const isSameScheduledDay =
        String(this.scheduledWorkCalendarEmployeeId) === String(idEmployee) &&
        this.scheduledWorkCalendarDate === workDate;
      this.scheduledWorkCalendarRequestGeneration = requestGeneration;
      this.scheduledWorkCalendarItems = [];
      this.scheduledWorkCalendarLoaded = false;
      if (!isSameScheduledDay) this.scheduledWorkCalendarTotalRows = 0;
      this.scheduledWorkCalendarLoading = true;
      try {
        const params = getParamsAll([
          `page,${page}`,
          `rows,${this.scheduledWorkCalendarRows}`,
          `idEmployee,${idEmployee}`,
          `dateFrom,${selectedDate
            .clone()
            .startOf("day")
            .format("YYYY-MM-DD HH:mm:ss")}`,
          `dateTo,${selectedDate
            .clone()
            .endOf("day")
            .format("YYYY-MM-DD HH:mm:ss")}`,
        ]);
        const response = await getWorkCalendarByEmployeeByRange(params);
        if (
          this.scheduledWorkCalendarRequestGeneration !== requestGeneration ||
          String(this.employee.idEmployee) !== String(idEmployee)
        ) {
          return;
        }

        const pageData = this.extractWorkCalendarPage(response, page);
        this.scheduledWorkCalendarItems = pageData.items;
        this.scheduledWorkCalendarPage = pageData.currentPage;
        this.scheduledWorkCalendarTotalRows = pageData.totalRows;
        this.scheduledWorkCalendarEmployeeId = idEmployee;
        this.scheduledWorkCalendarDate = workDate;
        this.scheduledWorkCalendarLoaded = true;
        this.mergeScheduledItemsIntoWorkCalendar(pageData.items);
      } catch (error) {
        if (
          this.scheduledWorkCalendarRequestGeneration !== requestGeneration
        ) {
          return;
        }
        console.error("Error loading scheduled work calendar", error);
        this.alertToast(
          "danger",
          this.$t("Alert"),
          error.message,
          "AlertTriangleIcon",
        );
      } finally {
        if (
          this.scheduledWorkCalendarRequestGeneration === requestGeneration
        ) {
          this.scheduledWorkCalendarLoading = false;
        }
      }
    },
    mergeWorkCalendarRange(items, dateFrom, dateTo) {
      const from = dateFrom.format("YYYY-MM-DD");
      const to = dateTo.format("YYYY-MM-DD");
      const isInsideRange = (item) => {
        const date = this.calendarDate(item?.workDate);
        return date && date >= from && date <= to;
      };
      const dirtyDates = new Set(this.workCalendarDirtyDates);
      const deletedDates = new Set(this.workCalendarDeletedDates);
      const currentItems = Array.isArray(this.employee.workCalendar)
        ? this.employee.workCalendar
        : [];
      const preservedItems = currentItems.filter(
        (item) =>
          !isInsideRange(item) ||
          dirtyDates.has(this.calendarDate(item.workDate)),
      );
      const remoteItems = items.filter((item) => {
        const date = this.calendarDate(item?.workDate);
        return (
          isInsideRange(item) &&
          date &&
          !dirtyDates.has(date) &&
          !deletedDates.has(date)
        );
      });
      const entriesByDate = new Map();

      [...preservedItems, ...remoteItems].forEach((item) => {
        const date = this.calendarDate(item?.workDate);
        if (date) entriesByDate.set(date, item);
      });

      this.employee.workCalendar = [...entriesByDate.values()].sort((a, b) =>
        this.calendarDate(a.workDate).localeCompare(
          this.calendarDate(b.workDate),
        ),
      );
    },
    async loadWorkCalendarRange(range) {
      if (
        this.option === 1 ||
        !this.employee.idEmployee ||
        !this.isCalendarSchedule ||
        !range?.start ||
        !range?.end
      ) {
        return;
      }

      const dateFrom = moment(range.start).startOf("day");
      const dateTo = moment(range.end).subtract(1, "day").endOf("day");
      if (!dateFrom.isValid() || !dateTo.isValid()) return;

      const rangeKey = `${dateFrom.format("YYYY-MM-DD")}:${dateTo.format(
        "YYYY-MM-DD",
      )}`;
      if (
        this.workCalendarLoadedRanges.includes(rangeKey) ||
        this.workCalendarPendingRanges.includes(rangeKey)
      ) {
        return;
      }

      const requestedEmployeeId = this.employee.idEmployee;
      const requestGeneration = this.workCalendarRequestGeneration;
      this.workCalendarPendingRanges.push(rangeKey);
      try {
        const params = getParamsAll([
          "page,1",
          `rows,${this.workCalendarRangeRows}`,
          `idEmployee,${this.employee.idEmployee}`,
          `dateFrom,${dateFrom.format("YYYY-MM-DD HH:mm:ss")}`,
          `dateTo,${dateTo.format("YYYY-MM-DD HH:mm:ss")}`,
        ]);
        const response = await getWorkCalendarByEmployeeByRange(params);
        if (
          this.workCalendarRequestGeneration !== requestGeneration ||
          String(this.employee.idEmployee) !== String(requestedEmployeeId) ||
          !this.isCalendarSchedule
        ) {
          return;
        }
        this.mergeWorkCalendarRange(
          this.extractWorkCalendarItems(response),
          dateFrom,
          dateTo,
        );
        this.workCalendarLoadedRanges.push(rangeKey);
      } catch (error) {
        if (this.workCalendarRequestGeneration !== requestGeneration) return;
        console.error("Error loading work calendar", error);
        this.alertToast(
          "danger",
          this.$t("Alert"),
          error.message,
          "AlertTriangleIcon",
        );
      } finally {
        if (this.workCalendarRequestGeneration === requestGeneration) {
          this.workCalendarPendingRanges =
            this.workCalendarPendingRanges.filter(
              (pendingRange) => pendingRange !== rangeKey,
            );
        }
      }
    },
    label(field) {
      return `pages.employees.form.label.${field}`;
    },

    labelHeader(field) {
      return `pages.employees.table.tableHeader.${field}`;
    },

    getTitle() {
      if (this.option === 1) return "register";
      else if (this.option === 2) return "show";
      else if (this.option === 3) return "edit";
    },

    async initialize() {
      this.option = this.$route.params.option;

      if (this.option === undefined) {
        router.push({ name: "employees" }).catch(() => {});
      }

      await this.loadCatData();

      switch (this.option) {
        case 1:
          if (Array.isArray(this.allDays) && this.allDays.length) {
            const allDaysOrdered = [...this.allDays].sort(
              (a, b) => diasSemana.indexOf(a.name) - diasSemana.indexOf(b.name),
            );
            this.employee.workSchedules = allDaysOrdered.map((day) => ({
              uuid: `${day.id}-${Date.now()}`,
              idCatDay: day.id,
              nameDay: day.name,
              startTime: null,
              endTime: null,
            }));
          } else {
            this.employee.workSchedules = [];
          }
          this.onCancel();
          this.employee.guidCompany =
            this.$store.state.appConfig.activeCompanyGuid;

          break;
        case 2:
        case 3:
          if (this.$route.params.guid === undefined) {
            router.push({ name: "employees" }).catch(() => {});
          }
          this.isSaveMode = this.option !== 2;
          await this.geEmployees(this.$route.params.guid);
          await this.getServicesEmployee(this.$route.params.guid);
          this.enableWizardFreeNavigation();
          break;
      }
    },
    enableWizardFreeNavigation() {
      if (!this.canNavigateWizardFreely) return;

      this.$nextTick(() => {
        if (this.$refs.form && this.$refs.form.activateAll) {
          this.$refs.form.activateAll();
        }
      });
    },
    async loadCatData() {
      showLoading();
      try {
        const paramRoles = this.chargeParams("roles");
        const paramDays = this.chargeParams("days");
        const paramScheduleTypes = this.chargeParams("scheduleTypes");

        const paramCharges = getParams([
          "page,1",
          "rows,999",
          `guidCompany,${this.$store.state.appConfig.activeCompanyGuid}`,
        ]);

        const [resultRoles, resultDay, resultScheduleTypes, resultCharges] =
          await Promise.all([
            getCatalog(paramRoles),
            getCatalog(paramDays),
            getCatalog(paramScheduleTypes),
            allCharges(paramCharges),
          ]);
        this.roles = _.get(resultRoles, "data.listOfItems.items");
        this.allDays = _.get(resultDay, "data.listOfItems.items");
        this.scheduleTypes =
          _.get(resultScheduleTypes, "data.listOfItems.items") || [];
        // Filtrar objetos vacíos y asegurar propiedad 'name'
        // this.roles = (_.get(resultRoles, "data.listOfItems.items") || [])
        //   .filter((item) => item && item.name) // Filtra objetos vacíos
        //   .map((item) => ({
        //     id: item.id,
        //     name: item.name,
        //   }));

        // this.allDays = (_.get(resultDay, "data.listOfItems.items") || [])
        //   .filter((item) => item && item.name) // Filtra objetos vacíos
        //   .map((item) => ({
        //     id: item.id,
        //     name: item.name,
        //   }));

        const charges = _.get(resultCharges, "data.items") || [];

        this.allCharges = charges.map((model) => ({
          id: model.idRol,
          name: model.name,
        }));
        stopLoading();
      } catch (error) {
        stopLoading();
        this.alertToast(
          "danger",
          this.$t("Alert"),
          error.message,
          "AlertTriangleIcon",
        );
        console.error("Error loading data", error.message);
      }
    },

    chargeParams(key) {
      let arrParams = [];
      arrParams.push(`itemsPage,${this.itemsPage}`);
      arrParams.push(`itemsRows,${this.itemsRows}`);

      switch (key) {
        case "roles":
          arrParams.push("guidCatalog, 4fbba07b-e201-45cf-9661-fd9f814fdddb");
          break;
        case "days":
          arrParams.push("guidCatalog, 2e42df02-8ea5-4e59-8180-51cd039e055a");
          break;
        case "scheduleTypes":
          // EMPLEADOS_TIPO_HORARIO
          arrParams.push("guidCatalog, 31c16bb8-9f88-4568-9d2d-baac3c674fd2");
          break;
      }
      return getParams(arrParams);
    },

    onSubmit() {
      try {
        this.validationEmployee().then((validate) => {
          if (validate) {
            showLoading();
            const idEmployee =
              this.option === 1 ? null : this.employee.idEmployee;
            let roles = [];
            let services = [];
            this.employee.services.map((item) => {
              const ser = {
                idService: item.idService,
              };
              services.push(ser);
            });
            this.employee.roles.map((item) => {
              const rol = {
                idRol: item,
              };
              roles.push(rol);
            });
            const workSchedules = this.isWeeklySchedule
              ? this.employee.workSchedules
                  .filter((schedule) => schedule.startTime && schedule.endTime)
                  .map((schedule) => ({
                    idCatDay: schedule.idCatDay,
                    startTime: schedule.startTime,
                    endTime: schedule.endTime,
                  }))
              : [];
            const workCalendar = this.isCalendarSchedule
              ? this.employee.workCalendar
                  .filter(
                    (schedule) =>
                      schedule.workDate &&
                      schedule.startTime &&
                      schedule.endTime,
                  )
                  .map((schedule) => ({
                    ...(this.option !== 1 && schedule.idWorkCalendar
                      ? {
                          idWorkCalendar: schedule.idWorkCalendar,
                          idEmployee:
                            schedule.idEmployee || this.employee.idEmployee,
                        }
                      : {}),
                    workDate: schedule.workDate,
                    startTime: schedule.startTime,
                    endTime: schedule.endTime,
                    notes: schedule.notes || null,
                  }))
              : [];
            let dateRepose =
              this.employee?.endDateRepose !== null &&
              this.employee?.endDateRepose !== undefined &&
              this.employee?.endDateRepose !== ""
                ? new Date(this.employee.endDateRepose).toISOString()
                : null;
            const body = {
              idEmployee: idEmployee,
              guidCompany: this.employee.guidCompany,
              name: this.employee.name,
              email: this.employee.email,
              phone: this.employee.phone,
              repose: this.employee.repose,
              endDateRepose: dateRepose,
              idCatScheduleType: this.employee.idCatScheduleType,
              workSchedules,
              workCalendar,
              roles: roles,
              services: services,
              isSucursal: this.employee.isSucursal,
              sucursalAdress: this.employee.sucursalAdress,
              urlPhoto: this.employee.urlPhoto || null,
              // idCatRole: this.employee.idCatRole,
              // roleName: this.employee.roleName,
            };
            createOrEditEmployee(body, idEmployee)
              .then(() => {
                this.alertToast(
                  "success",
                  this.$t("Alert"),
                  `${
                    this.option != 3
                      ? this.$t("registerSuccess")
                      : this.$t("updateRecord")
                  }`,
                  "CheckIcon",
                );
                this.goBack();
                stopLoading();
              })
              .catch((error) => {
                console.error("Error loading data", error.message);
                stopLoading();
                this.alertToast(
                  "danger",
                  this.$t("Alert"),
                  error.message,
                  "AlertTriangleIcon",
                );
              });
          }
        });
      } catch (error) {
        console.error("Error loading data", error.message);
        this.alertToast("danger", "Error", error.message, "AlertTriangleIcon");
      }
    },

    geEmployees(idEmployee) {
      this.resetWorkCalendarRequestState();
      showLoading();
      return getEmployee(idEmployee)
        .then((resp) => {
          stopLoading();
          const employee = _.get(resp, "data");
          const weeklyScheduleType = this.scheduleTypes.find(
            (scheduleType) =>
              String(scheduleType.name || "")
                .trim()
                .toLocaleLowerCase("es") === "semanal",
          );
          const workSchedules = employee.workSchedules || [];

          this.employee = Object.assign(this.employee, {
            idEmployee: employee.idEmployee || null,
            guidCompany: employee.guidCompany || null,
            name: employee.name || null,
            phone: employee.phone || "",
            email: employee.email || null,
            // idCatRole: employee.idCatRole || null,
            idCatScheduleType:
              employee.idCatScheduleType ||
              (workSchedules.length ? weeklyScheduleType?.id || null : null),
            workSchedules,
            workCalendar: [],
            repose: employee.repose || false,
            endDateRepose: employee.endDateRepose || "",
            roles: employee.roles.map((model) => model.idRol) || [],
            isSucursal: employee.isSucursal || null,
            sucursalAdress: employee.sucursalAdress || null,
            urlPhoto: employee.urlPhoto || null,
          });
        })
        .catch((error) => {
          console.log("error", error);
          stopLoading();
          this.alertToast(
            "danger",
            this.$t("Alert"),
            error.message,
            "AlertTriangleIcon",
          );
        });
    },

    onDropdownOpen(isOpen) {
      this.openTelInput = isOpen;
    },

    addSchedules() {
      if (
        !this.catDay ||
        [this.startTime, this.endTime].some((item) => item == null)
      ) {
        this.alertToast(
          "danger",
          this.$t("Alert"),
          this.$t("pages.companies.schedulesRequired"),
          "AlertTriangleIcon",
        );
        return;
      }

      const edit = this.employee.workSchedules.some(
        (schedule) =>
          schedule.uuid === this.idOrUuid ||
          schedule.idWorkingHours === this.idOrUuid,
      );

      if (edit) {
        const exists = this.employee.workSchedules.some((schedule) => {
          // const sameCatDay = schedule.idCatDay === this.catDay.id;
          // const isDifferentRecord =
          //   (this.idOrUuid === schedule.idWorkingHours &&
          //     schedule.idWorkingHours !== null) ||
          //   (this.idOrUuid === schedule.uuid &&
          //     schedule.idWorkingHours === null);

          // if (isDifferentRecord) return false;

          // return sameCatDay;
          const isCurrent =
            (this.idOrUuid === schedule.idWorkingHours &&
              schedule.idWorkingHours != null) ||
            (this.idOrUuid === schedule.uuid &&
              schedule.idWorkingHours == null);
          if (isCurrent) return false;
          return schedule.idCatDay === this.catDay.id;
        });

        if (exists) {
          this.alertToast(
            "danger",
            this.$t("Alert"),
            this.$t("pages.companies.scheduleAlreadyExists"),
            "AlertTriangleIcon",
          );
          return;
        }

        this.employee.workSchedules = this.employee.workSchedules.map(
          (schedule) => {
            const isCurrentSchedule =
              schedule.uuid === this.idOrUuid ||
              schedule.idWorkingHours === this.idOrUuid;

            if (isCurrentSchedule) {
              if (!this.catDay || typeof this.catDay !== "object") {
                return schedule;
              }

              return {
                ...schedule,
                idCatDay: this.catDay.id,
                nameDay: this.catDay.name,
                startTime: this.startTime,
                endTime: this.endTime,
              };
            }

            return schedule;
          },
        );

        // Limpiar campos
        this.catDay = Object.assign({});
        this.startTime = null;
        this.endTime = null;
        this.idOrUuid = null;
      } else {
        // Verificar si idCatDay ya existe en workSchedules
        const exists = this.employee.workSchedules.some(
          (schedule) => schedule.idCatDay === this.catDay.id,
        );
        if (exists) {
          this.alertToast(
            "danger",
            this.$t("Alert"),
            this.$t("pages.companies.scheduleAlreadyExists"),
            "AlertTriangleIcon",
          );
          return;
        }
        const random = () => ((1 + Math.random()) * 0x100000) | 0;
        const block = () =>
          (+moment.now() + random() + random() + random()).toString(16);
        const id = `${block()}-${block()}-${block()}`;
        // Agregar nuevo horario
        this.employee.workSchedules.push({
          uuid: id,
          idCatDay: this.catDay?.id,
          nameDay: this.catDay?.name,
          startTime: this.startTime,
          endTime: this.endTime,
        });

        // Limpiar campos
        this.catDay = Object.assign({});
        this.startTime = null;
        this.endTime = null;
        this.idOrUuid = null;
      }
    },

    onEditSchedules(item) {
      this.$swal({
        title: this.$t("areYouSure"),
        text: this.$t("willNotBeAble"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("yesEdit"),
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          if (item.uuid) {
            const index = this.employee.workSchedules.find(
              (model) => model.uuid === item.uuid,
            );
            console.log("item", item);
            this.editHoursMethod(item);
            // this.employee.workSchedules.splice(index, 1);
            // this.alertToast(
            //   "success",
            //   this.$t("Alert"),
            //   this.$t("delete_success"),
            //   "CheckIcon"
            // );
          } else {
            const index = this.employee.workSchedules.find(
              (model) => model.idWorkingHours === item.idWorkingHours,
            );
            this.editHoursMethod(item);
          }
        } else if (result.dismiss === "cancel") {
          this.message(
            this.$t("canceled"),
            this.$t("registerDeletedCanceled"),
            "error",
          );
        }
      });
    },

    onDeleteSchedules(item) {
      this.$swal({
        title: this.$t("areYouSure"),
        text: this.$t("willNotBeAble"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("yesDelete"),
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          if (item.uuid) {
            const index = this.employee.workSchedules.findIndex(
              (model) => model.uuid === item.uuid,
            );
            this.employee.workSchedules.splice(index, 1);
            this.alertToast(
              "success",
              this.$t("Alert"),
              this.$t("delete_success"),
              "CheckIcon",
            );
          } else {
            this.removeHoursMethod(item.idEmployee, item.idWorkingHours);
          }
        } else if (result.dismiss === "cancel") {
          this.message(
            this.$t("canceled"),
            this.$t("registerDeletedCanceled"),
            "error",
          );
        }
      });
    },

    removeHoursMethod(idEmployee, idWorkingHours) {
      try {
        showLoading();
        deleteWorkSchedules(idEmployee, idWorkingHours)
          .then((result) => {
            stopLoading();
            const index = this.employee.workSchedules.findIndex(
              (model) => model.idWorkingHours === idWorkingHours,
            );
            this.employee.workSchedules.splice(index, 1);
            this.alertToast(
              "success",
              this.$t("Alert"),
              this.$t("delete_success"),
              "CheckIcon",
            );
          })
          .catch((error) => {
            stopLoading();
            console.error("Error loading data", error.message);
            this.alertToast(
              "danger",
              "Error",
              error.message,
              "AlertTriangleIcon",
            );
          });
      } catch (error) {
        console.error("Error loading data", error.message);
        this.alertToast("danger", "Error", error.message, "AlertTriangleIcon");
      }
    },

    editHoursMethod(item) {
      // this.catDay.id = item.idCatDay;
      // this.catDay.name = item.nameDay;
      // this.startTime = item.startTime;
      // this.endTime = item.endTime;
      // this.idOrUuid = item?.uuid ? item.uuid : item?.idWorkingHours;
      const selectedDay = this.allDays.find((day) => day.id === item.idCatDay);
      this.catDay = selectedDay
        ? selectedDay
        : { id: item.idCatDay, name: item.nameDay };
      this.startTime = item.startTime;
      this.endTime = item.endTime;
      this.idOrUuid = item?.uuid ? item.uuid : item?.idWorkingHours;
    },

    formattedTime(time24) {
      let [hour, minute, second] = time24.split(":");
      hour = parseInt(hour);
      const ampm = hour >= 12 ? "PM" : "AM";

      hour = hour % 12 || 12;
      return `${hour}:${minute} ${ampm}`;
    },

    deleteFile(field) {
      this.employee[field] = null;
    },

    handleDragover(e) {
      e.stopPropagation();
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    },

    onFileChange(e, field) {
      if (e?.target?.files?.length > 0) {
        this.onTransFile(
          e?.target?.files,
          e.target.accept,
          e.target.multiple,
          field,
        );
      }
    },

    onTransFile(files, accept, multiple, field) {
      if (multiple) return;
      // this.loadingPhoto = true;
      files.forEach((file) => {
        if (!file) return;
        const type = file.type.split("/").pop();
        const isPdf = type === "pdf";
        const isImage = ["jpeg", "jpg", "png"].includes(type);
        if (!isPdf && !isImage) return;
        showLoading();
        postFile(file)
          .then((resp) => {
            this.employee[field] = resp.data;
          })
          .catch(console.error)
          .finally(() => {
            // this.loadingPhoto = false;
            stopLoading();
          });
      });
    },

    handleDrop(e, field) {
      e.stopPropagation();
      e.preventDefault();
      const { files } = e.dataTransfer;
      if (files.length !== 1) {
        return;
      }
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      const file = files[0];
      if (!allowedTypes.includes(file.type)) return;
      const accept =
        e.target.getAttribute("accept") ||
        e.target.parentElement.getAttribute("accept");
      const multiple =
        e.target.getAttribute("multiple") ||
        e.target.parentElement.getAttribute("multiple");
      this.onTransFile(files, ".png,.jpeg,.jpg", false, field);
    },

    goBack() {
      this.$refs.employeeRules.reset();
      router.back();
      this.onCancel();
    },

    onCancel() {
      this.resetWorkCalendarRequestState();
      Object.assign(this.employee, {
        idEmployee: "",
        guidCompany: "",
        name: "",
        phone: "",
        email: "",
        idCatRole: "",
        roleName: "",
        workSchedules:
          this.option === 1 && this.employee.workSchedules.length
            ? this.employee.workSchedules
            : [],
        workCalendar: [],
        idCatScheduleType: null,
        repose: false,
        endDateRepose: "",
        roles: [],
        isSucursal: false,
        sucursalAdress: "",
        urlPhoto: null,
      });
      this.catDay = Object.assign({});
      this.startTime = "";
      this.endTime = "";
    },

    validationEmployee() {
      return new Promise((resolve, reject) => {
        this.$refs.employeeRules.validate().then((success) => {
          if (success) {
            resolve(true);
          } else {
            reject();
          }
        });
      });
    },

    validationEmployeeTab() {
      if (this.canNavigateWizardFreely) return true;

      return this.validationEmployee();
    },

    message(title, text, icon) {
      this.$swal({
        title,
        text,
        icon,
        customClass: {
          confirmButton: "btn btn-success",
        },
      });
    },

    alertToast(type = null, title = null, message = null, icon) {
      this.$bvToast.toast(message, {
        title,
        variant: type,
        solid: true,
        icon,
      });
    },

    getServicesEmployee(idEmployee) {
      this.servicesEmployee = [];
      showLoading();
      getServicesByEmployee(idEmployee)
        .then((resp) => {
          stopLoading();
          const service = _.get(resp, "data");
          let listService = service?.items || [];
          listService.map((item) => {
            const body = {
              idService: item.service.idService,
              name: item.service.name,
            };
            this.servicesEmployee.push(body);
          });

          this.employee.services = this.servicesEmployee;
        })
        .catch((error) => {
          console.log("error", error);
          stopLoading();
          this.alertToast(
            "danger",
            this.$t("Alert"),
            error.message,
            "AlertTriangleIcon",
          );
        });
    },

    onSelecting(value) {
      this.itemSelected = value.idService;
    },

    onInput(newSelection) {
      const blocked = newSelection.filter(
        (opt) => opt.idService === this.itemSelected,
      );
      if (blocked.length > 1) {
        // Remove Option 2 from selection
        this.employee.services.pop();
        this.employee.services = [...this.employee.services];
      }
    },

    onSelected(value) {},

    isStatus(value) {
      return value ? "Activo" : "Inactivo";
    },

    changeSatus(e) {
      e.stopPropagation();
      e.preventDefault();
    },
    changeSatusSucursal(e) {
      e.stopPropagation();
      e.preventDefault();
    },
    isStatusSucursal(value) {
      return value ? "Si" : "No";
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "~@core/scss/vue/libs/vue-wizard.scss";

.schedule-mode-empty {
  padding: 4rem 1.5rem;
  border: 1px dashed #d8d6de;
  border-radius: 0.6rem;
  background: #fafafa;
}
</style>
